// @ts-strict-ignore
import { getProperty } from 'dot-prop';

import { populateThresholdData } from 'flow/service/thresholds';
import type {
  Action,
  BooleanRule,
  IndexedRule,
  RuleObject,
  Threshold,
  WorkflowProps,
} from '~/dashboard/views/workflows/store/engine/types';
import {
  LineRuleTypes,
} from '~/dashboard/views/workflows/store/engine/types';
import type { SemanticLine } from '../../NodeEditorDrawer/types';
import { CaretPositionSuffixTypes } from '../../NodeEditorDrawer/types';
import { getFlattenedPathAsArray, getRuleItemPathFromFlattenedPath } from '../../NodeEditorDrawer/ThresholdsTab/helpers';
import { createStringOfPathArray } from '~/dashboard/views/workflows/store/engine/utils';
import { getOperatorUsedInRuleObject } from '~/dashboard/views/workflows/store/engine/update/helpers';

export const getParentRulePathIndicies = pathIndices => pathIndices.slice(0, -1);
export const checkIfRuleHasAndKey = (rules: RuleObject) => ('and' in rules && rules.and);
export const checkIfRuleHasOrKey = (rules: RuleObject) => ('or' in rules && rules.or);

export const getLineIndices = ({
  semanticLineBreakData, lineNumberIndex,
}: {
  semanticLineBreakData: {
    groupedByLines: SemanticLine[];
    lineBreakPointIndices: string[];
  };
  lineNumberIndex: number;
}) => (
  semanticLineBreakData?.groupedByLines[lineNumberIndex]?.items
);

export const getRuleFromLine = ({
  sourceAction,
  lineIndices,
}: {
  sourceAction: Action;
  lineIndices: string[];
}): {
  path: string;
  value: string | RuleObject;
  type: LineRuleTypes;
} => {
  const targetItemPath = lineIndices[1] || lineIndices[0]; // groupEnd parenthesis line only has one in a line
  const targetItemFlattenedPath = getRuleItemPathFromFlattenedPath(targetItemPath);
  const flattenedPathInArray = getFlattenedPathAsArray(targetItemFlattenedPath);
  const finalFlattenedPathInArray = flattenedPathInArray
    .slice(0, flattenedPathInArray.length - 1); // last item in this path would be extra -0, -1, ... for dropdown mapping. in here, we only need the threshold location as in path
  const rulePath = createStringOfPathArray(finalFlattenedPathInArray);

  const rule = getProperty(sourceAction.rules, rulePath) as RuleObject;
  if ('id' in rule) {
    return {
      path: rulePath,
      value: rule.id,
      type: LineRuleTypes.THRESHOLD,
    };
  }

  if (Array.isArray(rule)) {
    let updatedRulePathForGroup = createStringOfPathArray(flattenedPathInArray);
    if (targetItemPath.endsWith(CaretPositionSuffixTypes.GROUP_START)) {
      // in this case, we want the path to be pointing the index 0 of that group where the parenthesis is starting
      const rulePointedByParenthesis = getProperty(
        sourceAction.rules, updatedRulePathForGroup) as IndexedRule | BooleanRule;

      const ruleOperator = getOperatorUsedInRuleObject({ rule: rulePointedByParenthesis });
      if (ruleOperator) {
        updatedRulePathForGroup = `${updatedRulePathForGroup}.${ruleOperator}`;
      }
    }

    return {
      path: updatedRulePathForGroup,
      value: rule as RuleObject,
      type: LineRuleTypes.RULE_GROUP,
    };
  }

  return undefined;
};

export const getThresholdFromActionUsingRulePath = ({
  sourceAction,
  thresholds,
  rulePath,
  actions,
  reasonCodeGroups,
  services,
}: {
  sourceAction: Action;
  thresholds: Record<string, Threshold>;
  rulePath: string;
  actions: Record<string, Action>;
  reasonCodeGroups: WorkflowProps['reasonCodeGroups'];
  services: WorkflowProps['services'];
}) => {
  const rules = ('id' in (sourceAction.rules as RuleObject)
    ? sourceAction.rules as IndexedRule
    : getProperty(sourceAction.rules, rulePath) as IndexedRule
  );
  const threshold = populateThresholdData({
    actions,
    reasonCodeGroups,
    services,
    threshold: thresholds[rules.id],
  });
  return threshold;
};

export const findCaretPosition = (lineItems: string[], caretPositionType: string) => (
  lineItems.find(item => item.endsWith(caretPositionType))
);
